import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Check, Copy, TriangleAlert } from 'lucide-react';
import { BottomSheet } from '@/components/ui/dialog';
import { haptic } from '@/lib/telegram';
import { reportError } from '@/lib/error-report';

type Phase = 'idle' | 'sending' | 'sent' | 'failed';

/**
 * Форма явной жалобы «Сообщить об ошибке» (kind=user_report). Открывается из
 * persistent-тоста в __root при 5xx/сетевой ошибке. Тех-контекст прикладывается
 * автоматически; юзер может (необязательно) описать, что делал.
 */
export function ErrorReportSheet({
  error,
  open,
  onClose,
}: {
  error: unknown;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [description, setDescription] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [code, setCode] = useState('');

  const submit = async () => {
    setPhase('sending');
    haptic('light');
    const res = await reportError(error, {
      kind: 'user_report',
      severity: 'error',
      description: description.trim() || undefined,
    });
    setCode(res.reportCode);
    setPhase(res.ok ? 'sent' : 'failed');
    haptic(res.ok ? 'success' : 'error');
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(code).catch(() => {});
    haptic('light');
  };

  const close = () => {
    onClose();
    // сброс для следующего открытия
    setTimeout(() => {
      setPhase('idle');
      setDescription('');
      setCode('');
    }, 300);
  };

  return (
    <BottomSheet open={open} onClose={close} title={t('Report a problem')}>
      {phase === 'sent' || phase === 'failed' ? (
        <div className="space-y-4 pb-2 text-center">
          <div
            className="mx-auto w-14 h-14 rounded-full flex items-center justify-center"
            style={{
              background:
                phase === 'sent'
                  ? 'color-mix(in srgb, var(--t-ok) 14%, transparent)'
                  : 'color-mix(in srgb, var(--t-danger) 14%, transparent)',
            }}
          >
            {phase === 'sent' ? (
              <Check size={26} color="var(--t-ok)" />
            ) : (
              <TriangleAlert size={26} color="var(--color-destructive)" />
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {phase === 'sent' ? t('Thanks, developers will look into it') : t('Could not send, save the code')}
          </p>
          <CodeChip code={code} label={t('Report code')} onCopy={copyCode} copyLabel={t('Copy')} />
          <button
            type="button"
            onClick={close}
            className="w-full calm-cta h-[52px] rounded-[var(--r-btn)] text-[16px] font-semibold active:scale-[0.98] transition-transform"
          >
            {t('Close')}
          </button>
        </div>
      ) : (
        <div className="space-y-4 pb-2">
          <p className="text-sm text-muted-foreground">
            {t('Send the details to developers so they can fix it faster')}
          </p>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('Describe what happened (optional)')}
            rows={3}
            maxLength={2000}
            className="w-full rounded-[var(--r-card)] bg-[var(--t-surface-2)] border border-[var(--t-line)] px-4 py-3 text-[16px] text-foreground placeholder:text-[var(--t-muted)] resize-none focus:outline-none focus:border-[var(--t-line)]"
          />
          <button
            type="button"
            onClick={submit}
            disabled={phase === 'sending'}
            className="w-full calm-cta h-[52px] rounded-[var(--r-btn)] text-[16px] font-semibold active:scale-[0.98] transition-transform flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Send size={16} />
            {phase === 'sending' ? t('Sending…') : t('Send report to developers')}
          </button>
        </div>
      )}
    </BottomSheet>
  );
}

function CodeChip({
  code,
  label,
  onCopy,
  copyLabel,
}: {
  code: string;
  label: string;
  onCopy: () => void;
  copyLabel: string;
}) {
  return (
    <div className="rounded-[var(--r-card)] bg-[var(--t-surface-2)] border border-[var(--t-line)] px-4 py-3">
      <div className="text-[12px] text-[var(--t-muted)] mb-1">{label}</div>
      <div className="flex items-center justify-between gap-3">
        <span className="select-all font-mono text-[18px] font-semibold">{code}</span>
        <button
          type="button"
          onClick={onCopy}
          className="flex items-center gap-1 text-xs text-foreground active:scale-95 transition-transform"
        >
          <Copy size={14} /> {copyLabel}
        </button>
      </div>
    </div>
  );
}
